import React, { Suspense, useEffect, useMemo, Component } from 'react';
import type { ReactNode } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { MiniAppErrorFallback } from './MiniAppErrorFallback';
import { AppDirectory } from './AppDirectory';
import { MRU_KEY } from '../constants';
import { z } from 'zod';
import { readStoredJson } from '../read-stored-json';
import { buildAppRegistry } from '../registry';
import { MiniAppBasePathProvider } from '../base-path';
import { resolveShellConfig } from '../shell-config';

interface ErrorBoundaryProps {
  appName: string;
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

// Exported so the Flow shell (the `/flow` host) can reuse the same fallback
// UI instead of maintaining a second copy.
export class MiniAppErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      return (
        <MiniAppErrorFallback
          error={this.state.error}
          appName={this.props.appName}
          onReset={() => this.setState({ error: null })}
        />
      );
    }
    return this.props.children;
  }
}

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-nightshade-500" />
  </div>
);

// Exported so the Flow shell can reuse the same dark-mode sync instead of a
// second implementation that could drift.
export function useMiniAppDarkMode() {
  const [isDark, setIsDark] = React.useState(() => {
    const theme = localStorage.getItem('theme') || localStorage.getItem('launcher-theme');
    return theme === 'dark' || (!theme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });

  // Apply the theme to <html> so all descendants (including portals) inherit
  // it, and leave it applied — never torn down on unmount. This hook runs
  // inside every mini-app shell (MiniAppLayout, FlowLayout, KeyedEditors),
  // all of which mount/unmount on ordinary route navigation; an unmount
  // cleanup that removed `.dark`/`data-theme` stripped the whole app's theme
  // back to light the moment a Flow/mini-app shell closed, even though
  // nothing about the user's stored preference changed (the dark-mode
  // flash on Flow→Home navigation). Terra's own body background already
  // follows the token via `bg-bg-primary`, which resolves to the same dark
  // color under `[data-theme='dark']` (see index.css), so no inline body
  // style is needed here either.
  React.useEffect(() => {
    // Glaze themes off [data-theme]; legacy Terra dark: off .dark — keep both in lockstep
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
  }, [isDark]);

  // Watch for external dark class changes (e.g. ThemeToggle in launcher)
  React.useEffect(() => {
    const observer = new MutationObserver(() => {
      const hasDark = document.documentElement.classList.contains('dark');
      setIsDark((prev: boolean) => prev !== hasDark ? hasDark : prev);
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      const theme = localStorage.getItem('theme') || localStorage.getItem('launcher-theme');
      if (!theme) setIsDark(mq.matches);
    };
    mq.addEventListener('change', handler);
    return () => { observer.disconnect(); mq.removeEventListener('change', handler); };
  }, []);

  return isDark;
}

export default function MiniAppLayout() {
  const apps = useMemo(() => buildAppRegistry(), []);
  const _isDark = useMiniAppDarkMode();

  const location = useLocation();
  const shellConfig = useMemo(
    () => resolveShellConfig(location.pathname, apps.map((a) => a.manifest)),
    [location.pathname, apps],
  );
  const railConfig = shellConfig?.rail;
  const shellBackground = railConfig?.backgroundColor;
  const hideDevGrab = shellConfig?.hideDevGrab ?? false;

  useEffect(() => {
    const match = location.pathname.match(/^\/tools\/([^/]+)/);
    if (!match) return;
    const slug = match[1];
    try {
      const mru = readStoredJson(localStorage, MRU_KEY, z.array(z.string()), []);
      const updated = [slug, ...mru.filter(s => s !== slug)].slice(0, 5);
      localStorage.setItem(MRU_KEY, JSON.stringify(updated));
    } catch { /* ignore */ }
  }, [location.pathname]);

  // Hide the DEV-only react-grab floating button on apps that opt out (presentation
  // surfaces). react-grab mounts a `[data-react-grab]` shadow host at <body> level;
  // a body attribute + global CSS rule hides it regardless of mount timing. No-op in
  // production builds where react-grab never loads.
  useEffect(() => {
    if (!hideDevGrab) return;
    document.body.setAttribute('data-hide-dev-grab', '');
    return () => document.body.removeAttribute('data-hide-dev-grab');
  }, [hideDevGrab]);

  if (apps.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        No tools available
      </div>
    );
  }

  return (
    <div className="flex h-full overflow-hidden bg-white dark:bg-nightshade-950">
      {/* Main area */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* App content. `ShellLayout` owns the rail and the mobile inset
            (`V2Shell` framed, `LegacyChrome` for Delphi). */}
        <div
          className="flex-1 min-h-0 overflow-auto bg-nightshade-50 dark:bg-nightshade-950"
          style={shellBackground ? { backgroundColor: shellBackground } : undefined}
        >
          <Routes>
            {/* App directory */}
            <Route index element={<AppDirectory apps={apps.filter(a => !a.manifest.hidden)} />} />

            {apps.map(({ manifest, Component }) => (
              <Route
                key={manifest.slug}
                path={`${manifest.slug}/*`}
                element={
                  <MiniAppBasePathProvider basePath={`/tools/${manifest.slug}`}>
                    <MiniAppErrorBoundary appName={manifest.name}>
                      <Suspense fallback={<LoadingSpinner />}>
                        <Component />
                      </Suspense>
                    </MiniAppErrorBoundary>
                  </MiniAppBasePathProvider>
                }
              />
            ))}
          </Routes>
        </div>
      </div>
    </div>
  );
}
